import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ETaxRuleType } from "@core/enums/tax-rule-type";
import { ToasterSeverities } from "@core/enums/toaster-severities";
import { DialogHelper } from "@core/helpers/dialog-helper";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { Dividend } from "@core/models/dividend-model";
import { RebalanceModel } from "@core/models/rebalance.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TaxRule } from "@core/models/tax-rule.model";
import { WithholdingConfig } from "@core/models/withholding-config.model";
import { DividendService } from "@core/services/dividend.service";
import { NotificationService } from "@core/services/notification.service";
import { RebalanceService } from "@core/services/rebalance.service";
import { SettingsService } from "@core/services/settings.service";
import { TableSorting } from "@custom-types/sorting";
import {
  dividendActions,
  rebalanceActions,
  settingsActions,
} from "@features/corporate-actions/store/corporate-actions.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concat,
  concatMap,
  EMPTY,
  finalize,
  forkJoin,
  mergeMap,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from "rxjs";

@Injectable()
export class CorporateActionsEffects {
  loadDividends$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dividendActions.LOAD_DIVIDENDS),
      mergeMap(
        (action: {
          searchTerm?: string;
          sorting?: TableSorting;
        }): Observable<Action> => {
          this.store.dispatch(
            dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: true })
          );
          return this.dividendService
            .getDividends$(action.searchTerm, action.sorting)
            .pipe(
              switchMap((dividendList: Array<Dividend>): Observable<Action> => {
                this.store.dispatch(
                  dividendActions.SET_DIVIDENDS_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(dividendActions.SET_DIVIDENDS({ dividendList }));
              }),
              catchError((): Observable<Action> => {
                return of(
                  dividendActions.SET_DIVIDENDS({ dividendList: [] }),
                  dividendActions.SET_DIVIDENDS_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  saveDividend$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dividendActions.SAVE_DIVIDEND),
      mergeMap(
        (action: {
          dividend: Partial<Dividend>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(
            dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: true })
          );
          return this.dividendService
            .saveDividend$(action.dividend, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  dividendActions.SET_DIVIDENDS_LOADING_STATUS({
                    loading: false,
                  })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(dividendActions.LOAD_DIVIDENDS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                this.store.dispatch(
                  dividendActions.SET_DIVIDENDS_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of();
              })
            );
        }
      )
    );
  });

  generateRecordAssignments$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dividendActions.GENERATE_RECORD_ASSIGNMENTS),
      mergeMap((action: { dividendId: string }): Observable<Action> => {
        this.store.dispatch(
          dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: true })
        );
        return this.dividendService
          .generateRecordAssignments$(action.dividendId)
          .pipe(
            switchMap((): Observable<Action> => {
              this.dialog.closeAll();
              this.store.dispatch(
                dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: false })
              );
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );
              return of(dividendActions.LOAD_DIVIDENDS({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response?.error
              );
              this.store.dispatch(
                dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: false })
              );
              return of();
            })
          );
      })
    );
  });

  postDividendResults$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(dividendActions.POST_DIVIDEND_RESULTS),
      mergeMap((action: { dividendId: string }): Observable<Action> => {
        this.store.dispatch(
          dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: true })
        );
        return this.dividendService
          .postDividendResults$(action.dividendId)
          .pipe(
            switchMap((): Observable<Action> => {
              this.store.dispatch(
                dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: false })
              );
              this.notificationService.show(
                ToasterSeverities.Success,
                "DIVIDEND_RESULTS_POSTED_SUCCESFULLY"
              );
              return of(dividendActions.LOAD_DIVIDENDS({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response?.error
              );
              this.store.dispatch(
                dividendActions.SET_DIVIDENDS_LOADING_STATUS({ loading: false })
              );
              return of();
            })
          );
      })
    );
  });

  // Rebalancing

  loadRebalanceData$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(rebalanceActions.LOAD_REBALANCE_DATA),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          rebalanceActions.LOAD_REBALANCE_DATA_LOADING({ loading: true })
        );
        return this.rebalanceService.getRebalanceData$(action.sorting).pipe(
          switchMap(
            (rebalanceData: Array<RebalanceModel>): Observable<Action> => {
              this.store.dispatch(
                rebalanceActions.LOAD_REBALANCE_DATA_LOADING({
                  loading: false,
                })
              );
              return of(
                rebalanceActions.LOAD_REBALANCE_DATA_SUCCESS({
                  rebalanceData,
                })
              );
            }
          ),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_SAVED",
              response?.error
            );
            return of(
              rebalanceActions.LOAD_REBALANCE_DATA_LOADING({
                loading: false,
              })
            );
          })
        );
      })
    );
  });

  deleteRebalance$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(rebalanceActions.DELETE_REBALANCE),
      mergeMap((action: { rebalanceId: string }): Observable<Action> => {
        this.store.dispatch(
          rebalanceActions.DELETE_REBALANCE_LOADING({ loading: true })
        );
        return this.rebalanceService.deleteRebalance$(action.rebalanceId).pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              rebalanceActions.DELETE_REBALANCE_LOADING({
                loading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "DATA_DELETE_SUCCESS"
            );
            this.store.dispatch(rebalanceActions.LOAD_REBALANCE_DATA({}));
            return of(
              rebalanceActions.DELETE_REBALANCE_SUCCESS({
                rebalanceId: action.rebalanceId,
              })
            );
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_DELETE_FAILED",
              response?.error
            );

            return of(
              rebalanceActions.DELETE_REBALANCE_LOADING({
                loading: false,
              })
            );
          })
        );
      })
    );
  });

  updateRebalanceExecutionDate$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE),
      mergeMap(
        (action: {
          rebalance: Partial<RebalanceModel>;
        }): Observable<Action> => {
          this.store.dispatch(
            rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_LOADING({
              loading: true,
            })
          );
          return this.rebalanceService
            .updateRebalanceExecutionDate$(action.rebalance)
            .pipe(
              switchMap((): Observable<Action> => {
                this.store.dispatch(
                  rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_LOADING({
                    loading: false,
                  })
                );
                this.store.dispatch(rebalanceActions.LOAD_REBALANCE_DATA({}));
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_UPDATE_SUCCESS"
                );
                return of(
                  rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_SUCCESS({
                    executionDate: action.rebalance.executionDate!,
                    rebalanceId: action.rebalance.id!,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_UPDATE_FAILED",
                  response?.error
                );

                return of(
                  rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_LOADING({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadRiskProfiles$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(rebalanceActions.LOAD_RISK_PROFILES),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.rebalanceService
          .getCompositionRiskProfiles$(action.id)
          .pipe(
            concatMap(
              (
                riskProfiles: Array<CompositionRiskProfile>
              ): Observable<Action> => {
                if (riskProfiles?.length) {
                  return this.rebalanceService
                    .getCompositionRiskProfiles$(action.id, true)
                    .pipe(
                      concatMap(
                        (
                          riskProfiles: Array<CompositionRiskProfile>
                        ): Observable<Action> => {
                          return of(
                            rebalanceActions.SET_RISK_PROFILES({
                              riskProfiles,
                            })
                          );
                        }
                      ),
                      catchError((): Observable<Action> => {
                        return of();
                      })
                    );
                }
                return this.rebalanceService.getRiskProfiles$().pipe(
                  concatMap(
                    (riskProfiles: Array<RiskProfile>): Observable<Action> => {
                      return of(
                        rebalanceActions.SET_RISK_PROFILES({
                          riskProfiles,
                        })
                      );
                    }
                  ),
                  catchError((): Observable<Action> => {
                    return of();
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  saveRebalance$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(rebalanceActions.SAVE_REBALANCE),
      mergeMap(
        (action: {
          rebalance: Partial<RebalanceModel>;
          id?: string;
        }): Observable<Action> => {
          this.store.dispatch(
            rebalanceActions.LOAD_REBALANCE_DATA_LOADING({ loading: true })
          );
          return this.rebalanceService
            .saveRebalance$(action.rebalance, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  rebalanceActions.LOAD_REBALANCE_DATA_LOADING({
                    loading: false,
                  })
                );
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(rebalanceActions.LOAD_REBALANCE_DATA({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return of(
                  rebalanceActions.LOAD_REBALANCE_DATA_LOADING({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadTaxRules$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(settingsActions.LOAD_TAX_RULE_LIST),
        mergeMap((action: { year?: number; taxType?: ETaxRuleType }) => {
          const loadingStartAction =
            action.taxType === ETaxRuleType.AnnualPayment
              ? settingsActions.SET_ANNUAL_PAYMENT_LOADING_STATUS({
                  loading: true,
                })
              : action.taxType === ETaxRuleType.LumpSumPayout
                ? settingsActions.SET_LUMP_SUM_PAYOUT_LOADING_STATUS({
                    loading: true,
                  })
                : null;

          const loadingEndAction =
            action.taxType === ETaxRuleType.AnnualPayment
              ? settingsActions.SET_ANNUAL_PAYMENT_LOADING_STATUS({
                  loading: false,
                })
              : action.taxType === ETaxRuleType.LumpSumPayout
                ? settingsActions.SET_LUMP_SUM_PAYOUT_LOADING_STATUS({
                    loading: false,
                  })
                : null;

          const actionsToDispatch: Action[] = [];

          if (loadingStartAction) actionsToDispatch.push(loadingStartAction);

          return this.settingsService
            .getTaxRuleList$(action.year, action.taxType)
            .pipe(
              switchMap((taxRuleList: Array<TaxRule>): Observable<Action> => {
                return action.taxType === ETaxRuleType.AnnualPayment
                  ? of(settingsActions.SET_ANNUAL_PAYMENT_LIST({ taxRuleList }))
                  : action.taxType === ETaxRuleType.LumpSumPayout
                    ? of(settingsActions.SET_LUMP_SUM_LIST({ taxRuleList }))
                    : EMPTY;
              }),
              catchError(() =>
                of(
                  settingsActions.SET_ANNUAL_PAYMENT_LIST({ taxRuleList: [] }),
                  settingsActions.SET_LUMP_SUM_LIST({ taxRuleList: [] })
                )
              ),
              finalize((): void => {
                if (loadingEndAction) this.store.dispatch(loadingEndAction);
              }),
              startWith(...actionsToDispatch)
            );
        })
      )
  );

  loadWithholdingConfigList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(settingsActions.LOAD_WITHHOLDING_CONFIG_LIST),
      switchMap((action: { year?: number }): Observable<Action> => {
        return this.settingsService.getWithholdingConfigList$(action.year).pipe(
          switchMap(
            (
              withholdingConfigList: Array<WithholdingConfig>
            ): Observable<Action> => {
              if (action.year) {
                return of(
                  settingsActions.SET_WITHHOLDING_CONFIG({
                    withholdingConfig: withholdingConfigList.pop(),
                  })
                );
              }
              return of(
                settingsActions.SET_WITHHOLDING_CONFIG_LIST({
                  withholdingConfigList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of(
              settingsActions.SET_WITHHOLDING_CONFIG_LIST({
                withholdingConfigList: [],
              })
            );
          }),
          finalize((): void =>
            this.store.dispatch(
              settingsActions.SET_WITHHOLDING_LOADING_STATUS({
                loading: false,
              })
            )
          ),
          startWith(
            settingsActions.SET_WITHHOLDING_LOADING_STATUS({ loading: true })
          )
        );
      })
    );
  });

  saveTaxRules$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(settingsActions.SAVE_TAX_RULES),
        mergeMap(
          (action: {
            taxRuleList: Array<Partial<TaxRule>>;
          }): Observable<Action> => {
            const taxRuleType: ETaxRuleType | undefined =
              action.taxRuleList[0]?.type;

            const loadingStartAction: Action | null =
              taxRuleType === ETaxRuleType.AnnualPayment
                ? settingsActions.SET_ANNUAL_PAYMENT_LOADING_STATUS({
                    loading: true,
                  })
                : taxRuleType === ETaxRuleType.LumpSumPayout
                  ? settingsActions.SET_LUMP_SUM_PAYOUT_LOADING_STATUS({
                      loading: true,
                    })
                  : null;

            const loadingEndAction: Action | null =
              taxRuleType === ETaxRuleType.AnnualPayment
                ? settingsActions.SET_ANNUAL_PAYMENT_LOADING_STATUS({
                    loading: false,
                  })
                : taxRuleType === ETaxRuleType.LumpSumPayout
                  ? settingsActions.SET_LUMP_SUM_PAYOUT_LOADING_STATUS({
                      loading: false,
                    })
                  : null;

            const saveObservables: Array<Observable<unknown>> =
              action.taxRuleList.map(
                (taxRule: Partial<TaxRule>): Observable<unknown> =>
                  this.settingsService
                    .saveTaxRule$(taxRule, taxRule["id"])
                    .pipe(
                      tap((): void =>
                        this.notificationService.show(
                          ToasterSeverities.Success,
                          "DATA_SAVED_SUCCESSFULLY"
                        )
                      ),
                      catchError(
                        (response: HttpErrorResponse): Observable<never> => {
                          this.notificationService.show(
                            ToasterSeverities.Error,
                            "DATA_COULD_NOT_BE_SAVED",
                            response?.error
                          );
                          return EMPTY;
                        }
                      )
                    )
              );

            return concat(
              loadingStartAction ? of(loadingStartAction) : EMPTY,
              forkJoin(saveObservables).pipe(
                switchMap((): Observable<Action> => {
                  const lastTaxRule: Partial<TaxRule> =
                    action.taxRuleList[action.taxRuleList.length - 1];
                  return of(
                    settingsActions.LOAD_TAX_RULE_LIST({
                      year: lastTaxRule.effectiveYear,
                      taxType: lastTaxRule.type,
                    })
                  );
                }),
                finalize((): void => {
                  if (loadingEndAction) this.store.dispatch(loadingEndAction);
                })
              )
            );
          }
        )
      )
  );

  saveWithholdingConfig$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(settingsActions.SAVE_WITHHOLDING_CONFIG),
      mergeMap(
        (action: {
          withholdingConfig: Partial<WithholdingConfig>;
        }): Observable<Action> => {
          return this.settingsService
            .saveWithholdingConfig$(
              action.withholdingConfig,
              action.withholdingConfig["id"]
            )
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                return of(
                  settingsActions.LOAD_WITHHOLDING_CONFIG_LIST({
                    year: action.withholdingConfig.effectiveYear,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return EMPTY;
              }),
              finalize((): void => {
                this.store.dispatch(
                  settingsActions.SET_WITHHOLDING_LOADING_STATUS({
                    loading: false,
                  })
                );
              }),
              startWith(
                settingsActions.SET_WITHHOLDING_LOADING_STATUS({
                  loading: true,
                })
              )
            );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private dividendService: DividendService,
    private rebalanceService: RebalanceService,
    private settingsService: SettingsService,
    private store: Store,
    private notificationService: NotificationService,
    private dialog: DialogHelper
  ) {}
}
